import React, { useState, useRef, useEffect } from "react";
import {
  Plus,
  MessageSquare,
  Send,
  User,
  Bot,
  Database,
  Settings,
  Code,
  FileText,
  Copy,
  AlertCircle,
  Layers,
  Upload,
  CheckCircle,
  GitMerge,
  Link,
  Workflow,
  Building2,
  BarChart3,
  Wrench,
  Eye,
  Search,
  Check,
  Shield,
  BarChart2,
  Network,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import SidebarFilters from "./sidefilter.js";
import logo1 from "../../assets/Image (3).jpg";


const AIChatInterface = () => {
  // Chats start empty
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      content: "I am BlueGecko Agile-to-Development AI Assistant → Translates Agile user stories directly into technical specifications and working code.",
    },
  ]);
  // Sidebar state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedMappingDocs, setSelectedMappingDocs] = useState([]);
  const [expandedOptions, setExpandedOptions] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const messagesEndRef = useRef(null);

  const iconMap = {
    database: Database,
    settings: Settings,
    code: Code,
    "file-text": FileText,
    layers: Layers,
    upload: Upload,
    "check-circle": CheckCircle,
    "git-merge": GitMerge,
    link: Link,
    workflow: Workflow,
    microsoft: Building2,
    chart: BarChart3,
    tool: Wrench,
    eye: Eye,
    search: Search,
    check: Check,
    shield: Shield,
    "chart-bar": BarChart2,
    diagram: Network,
    flow: Workflow,
    bot: Bot,
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Add this function before the AIChatInterface component
 // Replace your existing formatSQLResponse function with this fixed version
// Replace your formatSQLResponse function with this version that handles the nested structure
const formatSQLResponse = (content) => {
  if (!content) {
    return { type: "text", data: "No response received" };
  }

  const contentStr = typeof content === 'string' ? content : String(content);
  
  try {
    const parsed = JSON.parse(contentStr);

    if (typeof parsed === 'object' && parsed !== null) {
      // Check for multiple mapping file results like {"3": {"ddl": {...}}, "7": {"ddl": {...}}}
      const keys = Object.keys(parsed);
      let allDDLData = {};
      let allDMLData = {};
      let hasAnyDDL = false;
      let hasAnyDML = false;

      // Iterate through each mapping file result
     // Iterate through each mapping file result
keys.forEach(key => {
  const item = parsed[key];
  if (typeof item === 'object' && item !== null) {
    // Check for direct DDL/DML data
    if (item.ddl && typeof item.ddl === 'object') {
      Object.assign(allDDLData, item.ddl);
      hasAnyDDL = true;
    }
    
    if (item.dml && typeof item.dml === 'object') {
      Object.assign(allDMLData, item.dml);
      hasAnyDML = true;
    }

    // Check for language-specific data (python, sql, etc.)
    Object.keys(item).forEach(lang => {
      if (typeof item[lang] === 'object' && item[lang] !== null) {
        if (item[lang].ddl && typeof item[lang].ddl === 'object') {
          Object.assign(allDDLData, item[lang].ddl);
          hasAnyDDL = true;
        }
        
        if (item[lang].dml && typeof item[lang].dml === 'object') {
          Object.assign(allDMLData, item[lang].dml);
          hasAnyDML = true;
        }
      }
    });
  }
});

     // Return the appropriate type based on what we found
// If we have both DDL and DML, return a combined type
if (hasAnyDDL && hasAnyDML && Object.keys(allDDLData).length > 0 && Object.keys(allDMLData).length > 0) {
  return { type: "combined", data: { ddl: allDDLData, dml: allDMLData } };
}

if (hasAnyDDL && Object.keys(allDDLData).length > 0) {
  return { type: "ddl", data: allDDLData };
}

if (hasAnyDML && Object.keys(allDMLData).length > 0) {
  return { type: "dml", data: allDMLData };
}
      // Check for direct structure (fallback for single mapping file)
      if (parsed.ddl && typeof parsed.ddl === 'object') {
        return { type: "ddl", data: parsed.ddl };
      }

      if (parsed.dml && typeof parsed.dml === 'object') {
        return { type: "dml", data: parsed.dml };
      }

      // Generic JSON response
      return { type: "json", data: parsed };
    }

  } catch (error) {
    // Not JSON, check if it's SQL
    if (contentStr.includes('CREATE TABLE') || 
        contentStr.includes('INSERT INTO') || 
        contentStr.includes('UPDATE') || 
        contentStr.includes('DELETE FROM') ||
        contentStr.includes('SELECT')) {
      return { type: "sql", data: contentStr };
    }

    return { type: "text", data: contentStr };
  }

  return { type: "text", data: contentStr };
};

  // Add this component for rendering SQL
  const SQLRenderer = ({ sql }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
      navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between bg-gray-800 px-4 py-2">
          <span className="text-sm font-medium text-gray-300">SQL Script</span>
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600 transition-colors"
          >
            {copied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <pre className="p-4 text-sm text-gray-100 overflow-x-auto">
          <code>{sql}</code>
        </pre>
      </div>
    );
  };

  const DMLRenderer = ({ dmlData }) => {
    const [expandedTables, setExpandedTables] = useState({});
    const [copiedTable, setCopiedTable] = useState(null);

    const toggleTable = (tableName) => {
      setExpandedTables((prev) => ({ ...prev, [tableName]: !prev[tableName] }));
    };

    const copyTableDML = (tableName, dml) => {
      navigator.clipboard.writeText(dml);
      setCopiedTable(tableName);
      setTimeout(() => setCopiedTable(null), 2000);
    };

    const copyAllDML = () => {
      const allDML = Object.entries(dmlData)
        .filter(([_, dml]) => !dml.includes("not found"))
        .map(([_, dml]) => dml)
        .join("\n\n");
      navigator.clipboard.writeText(allDML);
      setCopiedTable("all");
      setTimeout(() => setCopiedTable(null), 2000);
    };

    const validTables = Object.entries(dmlData).filter(
      ([_, dml]) => !dml.includes("not found")
    );
    const invalidTables = Object.entries(dmlData).filter(([_, dml]) =>
      dml.includes("not found")
    );
    // Detect if this is Python code
const isPythonCode = Object.values(dmlData).some(code => 
  typeof code === 'string' && (
    code.includes('import pandas') || 
    code.includes('pd.read_sql') || 
    code.includes('import pyodbc')
  )
);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
<h3 className="text-lg font-semibold text-gray-800">
  {isPythonCode ? 'Python Data Scripts Generated' : 'DML Scripts Generated'}
</h3>
          <button
            onClick={copyAllDML}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {copiedTable === "all" ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copiedTable === "all" ? "Copied All!" : "Copy All"}
          </button>
        </div>

        {/* Valid Tables */}
        {validTables.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-green-700 mb-3">
              Successfully Generated ({validTables.length} tables)
            </h4>
            <div className="space-y-3">
              {validTables.map(([tableName, dml]) => (
                <div
                  key={tableName}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleTable(tableName)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTables[tableName] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
{isPythonCode ? (
  <Code className="w-4 h-4 text-green-600" />
) : (
  <Database className="w-4 h-4 text-green-600" />
)}                      <span className="font-medium text-gray-800">
                        {tableName}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyTableDML(tableName, dml);
                      }}
                      className="flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
                    >
                      {copiedTable === tableName ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      {copiedTable === tableName ? "Copied!" : "Copy"}
                    </button>
                  </div>

                  {expandedTables[tableName] && (
                    <div className="bg-gray-900 p-4">
                      <pre className="text-sm text-gray-100 overflow-x-auto">
                        <code>{dml}</code>
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invalid/Not Found Tables */}
        {invalidTables.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-orange-700 mb-3">
              Not Found ({invalidTables.length} tables)
            </h4>
            <div className="space-y-2">
              {invalidTables.map(([tableName, message]) => (
                <div
                  key={tableName}
                  className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg"
                >
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-gray-700">{tableName}</span>
                  <span className="text-xs text-orange-600 ml-auto">
                    {message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

const filtersSelected = selectedFilters.length > 0 && selectedMappingDocs.length > 0;




  // Add this component for rendering DDL collections
  const DDLRenderer = ({ ddlData }) => {
    const [expandedTables, setExpandedTables] = useState({});
    const [copiedTable, setCopiedTable] = useState(null);

    const toggleTable = (tableName) => {
      setExpandedTables((prev) => ({ ...prev, [tableName]: !prev[tableName] }));
    };

    const copyTableDDL = (tableName, ddl) => {
      navigator.clipboard.writeText(ddl);
      setCopiedTable(tableName);
      setTimeout(() => setCopiedTable(null), 2000);
    };

    const copyAllDDL = () => {
      const allDDL = Object.entries(ddlData)
        .filter(([_, ddl]) => !ddl.includes("not found"))
        .map(([_, ddl]) => ddl)
        .join("\n\n");
      navigator.clipboard.writeText(allDDL);
      setCopiedTable("all");
      setTimeout(() => setCopiedTable(null), 2000);
    };

    const validTables = Object.entries(ddlData).filter(
      ([_, ddl]) => !ddl.includes("not found")
    );
    const invalidTables = Object.entries(ddlData).filter(([_, ddl]) =>
      ddl.includes("not found")
    );
    // Detect if this is Python code
const isPythonCode = Object.values(ddlData).some(code => 
  typeof code === 'string' && (
    code.includes('import pandas') || 
    code.includes('pd.DataFrame') || 
    code.includes('import pyodbc')
  )
);

    

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
         <h3 className="text-lg font-semibold text-gray-800">
  {isPythonCode ? 'Python Schema Scripts Generated' : 'DDL Scripts Generated'}
</h3>
          <button
            onClick={copyAllDDL}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {copiedTable === "all" ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copiedTable === "all" ? "Copied All!" : "Copy All"}
          </button>
        </div>

        {/* Valid Tables */}
        {validTables.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-green-700 mb-3">
              Successfully Generated ({validTables.length} tables)
            </h4>
            <div className="space-y-3">
              {validTables.map(([tableName, ddl]) => (
                <div
                  key={tableName}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleTable(tableName)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedTables[tableName] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
{isPythonCode ? (
  <Code className="w-4 h-4 text-green-600" />
) : (
  <Database className="w-4 h-4 text-blue-600" />
)}                      <span className="font-medium text-gray-800">
                        {tableName}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyTableDDL(tableName, ddl);
                      }}
                      className="flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
                    >
                      {copiedTable === tableName ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      {copiedTable === tableName ? "Copied!" : "Copy"}
                    </button>
                  </div>

                  {expandedTables[tableName] && (
                    <div className="bg-gray-900 p-4">
                      <pre className="text-sm text-gray-100 overflow-x-auto">
                        <code>{ddl}</code>
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invalid/Not Found Tables */}
        {invalidTables.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-orange-700 mb-3">
              Not Found ({invalidTables.length} tables)
            </h4>
            <div className="space-y-2">
              {invalidTables.map(([tableName, message]) => (
                <div
                  key={tableName}
                  className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg"
                >
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-gray-700">{tableName}</span>
                  <span className="text-xs text-orange-600 ml-auto">
                    {message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  

  const handleKeyDown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};

  const createNewChat = () => {
    const newChat = {
      id: Date.now(),
      title: "New Chat",
      lastMessage: "",
      timestamp: "Just now",
      messages: [],
    };
    setChats([newChat, ...chats]);
    setCurrentChat(newChat.id);
    setMessages([]);
  };

  // Add this function in your AIChatInterface component, before the sendMessage function

 const generateDynamicPayload = (selectedFilters, mappingFileId, query) => {
  // Default values
  let operation = "extract";
  let type = "script";
  let language = "sql";

  // Map selected filters to payload parameters
  selectedFilters.forEach((filterId) => {
    // Extract operation from the filter ID pattern
    if (filterId.startsWith("extract")) {
      operation = "extract";
    } else if (filterId.startsWith("transform")) {
      operation = "transform";
    } else if (filterId.startsWith("load")) {
      operation = "load";
    }

    // Extract language (sql or python)
    if (filterId.includes("-sql")) {
      language = "sql";
    } else if (filterId.includes("-python")) {
      language = "python";
    }
  });

  // Build the dynamic payload - now supports multiple mapping files
  const payload = {
    mapping_file_id: Array.isArray(mappingFileId) ? mappingFileId : [mappingFileId], // Always send as array
    query: query,
    operation: operation,
    type: type,
    language: language,
  };

  // Add additional parameters based on other filter selections
  const additionalParams = {};

  selectedFilters.forEach((filterId) => {
    // Business automation filters
    if (filterId === "workflow-design") {
      additionalParams.workflow_type = "design";
    } else if (filterId === "erp-automation") {
      additionalParams.automation_type = "erp";
    } else if (filterId === "d365-automation") {
      additionalParams.automation_type = "d365";
    }

    // Data analytics filters
    if (filterId === "data-engineering") {
      additionalParams.analytics_type = "engineering";
    } else if (filterId === "data-modeling") {
      additionalParams.analytics_type = "modeling";
    } else if (filterId === "business-analytics") {
      additionalParams.analytics_type = "business";
    }

    // Data management filters
    if (filterId === "pii-identification") {
      additionalParams.management_type = "pii";
    } else if (filterId === "data-analysis") {
      additionalParams.management_type = "analysis";
    } else if (filterId === "data-quality") {
      additionalParams.management_type = "quality";
    }

    // Testing filters
    if (filterId === "test-automation") {
      additionalParams.test_type = "automation";
    }

    // Integration filters
    if (filterId === "api-integration") {
      additionalParams.integration_type = "api";
    }
  });

  // Merge additional parameters
  return { ...payload, ...additionalParams };
};

 const sendMessage = async () => {
  if (!message.trim()) return;

  const userMessage = {
    id: Date.now(),
    type: "user",
    content: message,
  };

  setMessages((prev) => [...prev, userMessage]);
  setMessage("");

  try {
    // Use all selected mapping docs or default to ["82"]
    const mappingFileId = selectedMappingDocs.length > 0 
      ? selectedMappingDocs.map(id => String(id)) // Convert all to strings
      : ["82"]; // Default as array

    // Generate dynamic payload based on selected filters
    const requestBody = generateDynamicPayload(
      selectedFilters,
      mappingFileId, // Pass the array of IDs
      message
    );

    // Log the payload for debugging (remove in production)
    console.log("Dynamic payload:", requestBody);

    const token = localStorage.getItem("token"); // or sessionStorage, depending on your login flow

   // Replace the response handling part in your sendMessage function with this:
const response = await fetch(
  "https://apicodecheetah.bluegecko.ai:8001/generate-from-query",
  {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  }
);

if (!response.ok) {
  throw new Error(`HTTP error! status: ${response.status}`);
}

const data = await response.json();
console.log("API Response:", data); // Debug log

// Handle the response properly
let botContent;
if (typeof data === "object" && data !== null) {
  // Check if it's already in the expected format
  if (data.ddl || data.dml) {
    botContent = JSON.stringify(data, null, 2);
  } else {
    // Check for nested structure
    const keys = Object.keys(data);
    if (keys.length === 1 && typeof data[keys[0]] === 'object') {
      botContent = JSON.stringify(data, null, 2);
    } else {
      botContent = JSON.stringify(data, null, 2);
    }
  }
} else {
  botContent = String(data);
}

const botMessage = {
  id: Date.now() + 1,
  type: "bot",
  content: botContent,
};
    setMessages((prev) => [...prev, botMessage]);

    // Update chat preview
    setChats((prev) =>
      prev.map((chat) =>
        chat.id === currentChat
          ? {
              ...chat,
              lastMessage: userMessage.content,
              timestamp: "Just now",
              messages: [...messages, userMessage, botMessage],
            }
          : chat
      )
    );
  } catch (error) {
    const errorMessage = {
      id: Date.now() + 1,
      type: "bot",
      content: "⚠️ Error connecting to API.",
    };
    setMessages((prev) => [...prev, errorMessage]);
  }
};

  // --- Sidebar logic ---
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories((prev) => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId],
    }));
  };

  const toggleOption = (optionId) => {
    setExpandedOptions((prev) => ({ ...prev, [optionId]: !prev[optionId] }));
  };

  const toggleType = (typeId) => {
    setExpandedTypes((prev) => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  const handleFilterSelect = (filterId) => {
    setSelectedFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId]
    );
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: "text-blue-600 bg-blue-50 border-blue-200",
      orange: "text-orange-600 bg-orange-50 border-orange-200",
      purple: "text-purple-600 bg-purple-50 border-purple-200",
      green: "text-green-600 bg-green-50 border-green-200",
    };
    return colorMap[color] || colorMap.blue;
  };

  const renderIcon = (iconName, className = "w-4 h-4") => {
    const IconComponent = iconMap[iconName] || Database;
    return <IconComponent className={className} />;
  };

  const renderFilterOptions = (options, level = 0) => {
    return options.map((option) => (
      <div key={option.id} className={`ml-${level * 2}`}>
        <div
          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-gray-50 ${
            selectedFilters.includes(option.id)
              ? "bg-blue-50 border border-blue-200"
              : ""
          }`}
          onClick={() => {
            if (option.types) {
              toggleOption(option.id);
            } else {
              handleFilterSelect(option.id);
            }
          }}
        >
          {option.types &&
            (expandedOptions[option.id] ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            ))}
          {renderIcon(option.icon)}
          <span className="text-sm font-medium">{option.label}</span>
        </div>

        {/* Render Types (Pipeline, Script) */}
        {expandedOptions[option.id] && option.types && (
          <div className="ml-4 mt-2 space-y-1">
            <div className="text-xs font-semibold text-gray-600 mb-2 pl-2">
              Types:
            </div>
            {option.types.map((type) => (
              <div key={type.id} className="ml-2">
                <div
                  className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-gray-50 ${
                    selectedFilters.includes(type.id)
                      ? "bg-blue-50 border border-blue-200"
                      : ""
                  }`}
                  onClick={() => {
                    if (type.languages) {
                      toggleType(type.id);
                    } else {
                      handleFilterSelect(type.id);
                    }
                  }}
                >
                  {type.languages &&
                    (expandedTypes[type.id] ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    ))}
                  {renderIcon(type.icon)}
                  <span className="text-sm">{type.label}</span>
                </div>

                {/* Render Languages (SQL, Python) */}
                {expandedTypes[type.id] && type.languages && (
                  <div className="ml-4 mt-2 space-y-1">
                    <div className="text-xs font-semibold text-gray-600 mb-2 pl-2">
                      Languages:
                    </div>
                    {type.languages.map((language) => (
                      <div key={language.id} className="ml-2">
                        <div
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-gray-50 ${
                            selectedFilters.includes(language.id)
                              ? "bg-blue-50 border border-blue-200"
                              : ""
                          }`}
                          onClick={() => handleFilterSelect(language.id)}
                        >
                          {renderIcon(language.icon)}
                          <span className="text-sm">{language.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  return (
<div className="flex bg-gray-50" style={{ height: 'calc(100vh - 140px)' }}>


<div className="w-64 bg-white border-r border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 140px)' }}
>

 <div className="p-4 border-b border-gray-200 flex justify-center">
  <button
    onClick={createNewChat}
    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
  >
    <Plus className="w-4 h-4" /> New Chat
  </button>
</div>



        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            {chats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => {
                  setCurrentChat(chat.id);
                  setMessages(chat.messages || []);
                }}
                className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${
                  currentChat === chat.id
                    ? "bg-blue-50 border border-blue-200"
                    : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-sm truncate">
                    {chat.title}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {chat.lastMessage}
                </p>
                <span className="text-xs text-gray-400">{chat.timestamp}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
<div className="flex-1 flex flex-col min-w-0">

        {/* Chat Header */}
        {/* <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-800">AI Assistant</h1>
              <p className="text-sm text-gray-500">How can I help you today?</p>
            </div>
            {selectedMappingDocs.length > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{selectedMappingDocs.length}</span> mapping object(s) selected
                </p>
                <p className="text-xs text-gray-500">
                  Primary: {selectedMappingDocs[0]}
                </p>
              </div>
            )}
          </div>
        </div> */}

        {/* Messages Area */}
<div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>




<div className="flex-1 overflow-y-auto px-4 py-1">

<div className="max-w-4xl mx-auto space-y-4 min-h-full">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-4 ${
                  msg.type === "user" ? "justify-end" : ""
                }`}
              >
                {msg.type === "bot" && (
                   <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                                    <img
                                      src={logo1}
                                      alt="AI Logo"
                                      className="w-full h-full object-cover"
                                    />{" "}
                                  </div>
                )}

                <div
                  className={`max-w-3xl p-4 rounded-lg ${
                    msg.type === "user"
                      ? "bg-blue-600 text-white ml-12"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  {msg.type === "bot" ? (
                    (() => {
                      const formatted = formatSQLResponse(msg.content);

                      switch (formatted.type) {
                          case "combined":
    return (
      <div className="space-y-6">
        <DDLRenderer ddlData={formatted.data.ddl} />
        <DMLRenderer dmlData={formatted.data.dml} />
      </div>
    );
                        case "ddl":
                          return <DDLRenderer ddlData={formatted.data} />;
                        case "dml":
                          return <DMLRenderer dmlData={formatted.data} />;
                        case "sql":
                          return <SQLRenderer sql={formatted.data} />;
                        case "json":
                          return (
                            <div>
                              <div className="text-sm font-medium text-gray-600 mb-2">
                                Response:
                              </div>
                              <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                                <code>
                                  {JSON.stringify(formatted.data, null, 2)}
                                </code>
                              </pre>
                            </div>
                          );
                        default:
                          return (
                            <div className="whitespace-pre-wrap">
                              {formatted.data}
                            </div>
                          );
                      }
                    })()
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  )}
                </div>

                {msg.type === "user" && (
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
            </div> 
          </div>
        </div>

        {/* Message Input */}
<div className="bg-white border-t border-gray-200 p-2 mt-auto">



          <div className="max-w-4xl mx-auto">
            <div className="flex gap-4">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}  // Add this line
                  placeholder={
                    filtersSelected
                      ? "Type your message..."
                      : "Please select filters to enable chat"
                  }
                  disabled={!filtersSelected}
                  className={`flex-1 px-4 py-2 rounded-lg border focus:outline-none ${
                    filtersSelected
                      ? "border-gray-300 focus:ring-2 focus:ring-blue-500"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                />
              <button
                onClick={sendMessage}
                disabled={!filtersSelected || !message.trim()}
                className={`ml-2 px-4 py-2 rounded-lg ${
                  filtersSelected
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Filters */}
      <SidebarFilters
        selectedFilters={selectedFilters}
        selectedMappingDocs={selectedMappingDocs}
        setSelectedMappingDocs={setSelectedMappingDocs}
        expandedCategories={expandedCategories}
        expandedSubcategories={expandedSubcategories}
        expandedOptions={expandedOptions}
        expandedTypes={expandedTypes}
        toggleCategory={toggleCategory}
        toggleSubcategory={toggleSubcategory}
        toggleOption={toggleOption}
        toggleType={toggleType}
        handleFilterSelect={handleFilterSelect}
        renderIcon={renderIcon}
        renderFilterOptions={renderFilterOptions}
        getColorClasses={getColorClasses}
      />
    </div>
  );
};

export default AIChatInterface;
